import { useMemo } from "react";

/**
 * Check if symbol is an Option contract (ends strictly with CE or PE)
 */
export function isOptionSymbol(symbol) {
  if (!symbol) return false;
  const s = String(symbol).toUpperCase().trim();
  return (s.endsWith("CE") || s.endsWith("PE")) && !s.endsWith("FUT") && !s.endsWith("FUTURES");
}

/**
 * Check if symbol is a Commodity symbol
 */
export function isCommoditySymbol(symbol) {
  if (!symbol) return false;
  const s = String(symbol).toUpperCase().trim();
  if (s.includes("MCX")) return true;
  const commodities = [
    "CRUDEOIL", "GOLD", "SILVER", "NATURALGAS", "COPPER",
    "ZINC", "LEAD", "ALUMINIUM", "MENTHAOIL", "COTTON"
  ];
  const cleanSymbol = s.replace(/^MCX:/i, "").replace(/^NFO:/i, "").replace(/^BFO:/i, "").replace(/^NSE:/i, "").replace(/^BSE:/i, "");
  return commodities.some(comm => cleanSymbol.startsWith(comm));
}

/**
 * Check if symbol is an Index symbol
 */
export function isIndexSymbol(symbol) {
  if (!symbol) return false;
  const s = String(symbol).toUpperCase().trim();
  if (!isOptionSymbol(s)) return false;
  return (
    s.includes("NIFTY") ||
    s.includes("BANKNIFTY") ||
    s.includes("FINNIFTY") ||
    s.includes("MIDCPNIFTY") ||
    s.includes("MIDCP") ||
    s.includes("SENSEX") ||
    s.includes("BANKEX")
  );
}

/**
 * Extract stock name from a stock option symbol
 * @param {string} symbol - e.g. "NSE:RELIANCE25SEP1400CE"
 * @returns {string|null} - e.g. "RELIANCE"
 */
export function getStockFromSymbol(symbol) {
  try {
    if (!symbol) return null;
    const s = String(symbol).toUpperCase().trim();

    // Must strictly be an option contract (ends with CE or PE)
    if (!isOptionSymbol(s)) return null;

    // Exclude Index options
    if (isIndexSymbol(s)) return null;

    // Exclude MCX/Commodity options
    if (isCommoditySymbol(s)) return null;

    const cleanSymbol = s.replace(/^NSE:/i, "").replace(/^BSE:/i, "").replace(/^NFO:/i, "").replace(/^BFO:/i, "").replace(/^MCX:/i, "");
    const match = cleanSymbol.match(/^([A-Z]+)/);
    return match ? match[1] : null;
  } catch (e) {
    console.error("Error extracting stock from symbol:", symbol, e);
    return null;
  }
}

/**
 * Normalize side
 */
function normalizeSide(rawSide) {
  if (rawSide === null || rawSide === undefined) return null;
  const s = String(rawSide).trim().toLowerCase();
  if (s === "1" || s === "buy" || s === "b") return "buy";
  if (s === "2" || s === "-1" || s === "sell" || s === "s") return "sell";
  return null;
}

/**
 * Hook to view Stock Options Positions
 */
export function useStockOptionsPositionsView(
  positions,
  orders,
  userMappings,
  positionsUpdateVersion
) {
  const stockOptionsViewData = useMemo(() => {
    if (!positions || positions.length === 0) {
      return { data: [], baseQtyMap: {}, activeStocks: [], symbolStrategyBaseQty: {} };
    }

    // Filter only stock options positions for client CA00246
    const stockPositions = positions.filter((p) => {
      const clientId = p.client_id || p.clientId;
      if (clientId !== "CA00246") return false;
      const stock = getStockFromSymbol(p.symbol);
      const netQty = parseFloat(p.net_qty ?? p.qty ?? 0);
      return stock !== null && Math.abs(netQty) > 0;
    });

    // Extract all unique stock symbols present
    const stockSet = new Set();
    stockPositions.forEach((p) => {
      const stock = getStockFromSymbol(p.symbol);
      if (stock) stockSet.add(stock);
    });

    const activeStocks = Array.from(stockSet);

    // Group positions by client and stock symbol
    const clientStockMap = {};

    stockPositions.forEach((p) => {
      const clientId = p.client_id || p.clientId;
      const stock = getStockFromSymbol(p.symbol);
      if (!clientId || !stock) return;

      if (!clientStockMap[clientId]) {
        clientStockMap[clientId] = {};
      }

      if (!clientStockMap[clientId][stock]) {
        clientStockMap[clientId][stock] = {
          qty: 0,
          slQty: 0,
          pnl: 0,
          positions: []
        };
      }

      const qty = Math.abs(parseFloat(p.net_qty ?? p.qty ?? 0));
      const pnl = parseFloat(p.pl ?? p.pnl ?? 0);

      clientStockMap[clientId][stock].qty += qty;
      clientStockMap[clientId][stock].pnl += pnl;
      clientStockMap[clientId][stock].positions.push(p);
    });

    // Process matching sell orders for SL Qty
    if (orders && orders.length > 0) {
      orders.forEach((o) => {
        if (normalizeSide(o.side) !== "sell") return;
        const clientId = o.clientId || o.client_id;
        if (clientId !== "CA00246") return;
        const stock = getStockFromSymbol(o.symbol);
        if (!stock) return;

        if (!clientStockMap[clientId]) {
          clientStockMap[clientId] = {};
        }
        if (!clientStockMap[clientId][stock]) {
          clientStockMap[clientId][stock] = {
            qty: 0,
            slQty: 0,
            pnl: 0,
            positions: []
          };
        }
        clientStockMap[clientId][stock].slQty += Math.abs(parseFloat(o.quantity || o.qty || 0));
      });
    }

    // Format output data array matching client user mappings
    const data = [];
    Object.keys(clientStockMap).forEach((clientId) => {
      let userMapping = null;
      if (userMappings) {
        const matches = userMappings.filter((u) => u.client_id === clientId);
        userMapping = matches.find((u) => (u.broker || "").toLowerCase() === "dhan_xts") || matches[0];
      }

      const rowData = {
        clientId,
        clientName: userMapping ? userMapping.name : `Client ${clientId}`,
        status: userMapping ? userMapping.status : "unknown",
        broker: userMapping ? userMapping.broker : "unknown",
        totalMultiplier: 1,
      };

      activeStocks.forEach((stock) => {
        if (clientStockMap[clientId][stock]) {
          const stockInfo = clientStockMap[clientId][stock];
          const qty = stockInfo.qty;
          const slQty = stockInfo.slQty;
          const pnl = stockInfo.pnl;

          let color = "gray";
          if (qty > 0) {
            if (slQty === qty) color = "green";
            else if (slQty > 0 || slQty < qty) color = "red";
          }

          const stockObj = {
            qty,
            slQty,
            pnl,
            color,
            multiplier: 1,
            positions: stockInfo.positions
          };

          rowData[stock] = stockObj;
          rowData[stock.toLowerCase()] = stockObj;
          rowData[stock.toUpperCase()] = stockObj;
        }
      });

      data.push(rowData);
    });

    const symbolStrategyBaseQty = {};
    stockPositions.forEach((p) => {
      const stock = getStockFromSymbol(p.symbol);
      if (!stock) return;
      const key = `${p.symbol}_${p.strategy || "NO_STRATEGY"}`;
      if (!symbolStrategyBaseQty[key]) {
        const netQty = Math.abs(parseFloat(p.net_qty ?? p.qty ?? 0));
        symbolStrategyBaseQty[key] = {
          symbol: p.symbol,
          strategy: p.strategy,
          index: stock,
          baseQty: netQty,
          clients: 1
        };
      }
    });

    return {
      data,
      baseQtyMap: {},
      activeStocks,
      symbolStrategyBaseQty
    };
  }, [positions, orders, userMappings, positionsUpdateVersion]);

  return stockOptionsViewData;
}

/**
 * Hook to view Stock Options Orders
 */
export function useStockOptionsOrdersView(
  orders,
  userMappings,
  ordersUpdateVersion,
  users
) {
  const stockOptionsOrderData = useMemo(() => {
    if (!orders || orders.length === 0) {
      return { data: [], activeStocks: [], activeIndexes: [], buyOrdersCount: 0, totalOrdersCount: 0 };
    }

    // Filter stock option orders for client CA00246 only
    const stockOrders = orders.filter((o) => {
      const clientId = o.clientId || o.client_id;
      if (clientId !== "CA00246") return false;
      const stock = getStockFromSymbol(o.symbol);
      return stock !== null;
    });

    const buyOrders = stockOrders.filter((o) => {
      const side = String(o.side ?? "").trim().toLowerCase();
      return side === "1" || side === "buy" || side === "b";
    });

    // Group buy orders by client and stock symbol
    const clientStockMap = {};

    buyOrders.forEach((o) => {
      const clientId = o.clientId || o.client_id;
      const stock = getStockFromSymbol(o.symbol);
      if (!clientId || !stock) return;

      if (!clientStockMap[clientId]) {
        clientStockMap[clientId] = {};
      }

      if (!clientStockMap[clientId][stock]) {
        clientStockMap[clientId][stock] = {
          qty: 0,
          orders: []
        };
      }

      const qty = Math.abs(parseFloat(o.quantity || o.qty || 0));
      clientStockMap[clientId][stock].qty += qty;
      clientStockMap[clientId][stock].orders.push(o);
    });

    const activeStocks = Array.from(
      new Set(
        buyOrders
          .map((o) => getStockFromSymbol(o.symbol))
          .filter(Boolean)
      )
    );

    const data = [];
    Object.keys(clientStockMap).forEach((clientId) => {
      let userMapping = null;
      if (userMappings) {
        const matches = userMappings.filter((u) => u.client_id === clientId);
        userMapping = matches.find((u) => (u.broker || "").toLowerCase() === "dhan_xts") || matches[0];
      }

      const rowData = {
        clientId,
        clientName: userMapping ? userMapping.name : `Client ${clientId}`,
        status: userMapping ? userMapping.status : "unknown",
        broker: userMapping ? userMapping.broker : "unknown",
        totalMultiplier: 1,
      };

      activeStocks.forEach((stock) => {
        if (clientStockMap[clientId][stock]) {
          const stockInfo = clientStockMap[clientId][stock];
          const stockObj = {
            qty: stockInfo.qty,
            color: "green",
            multiplier: 1,
            orders: stockInfo.orders
          };

          rowData[stock] = stockObj;
          rowData[stock.toLowerCase()] = stockObj;
          rowData[stock.toUpperCase()] = stockObj;
        }
      });

      data.push(rowData);
    });

    const symbolStrategyBaseQty = {};
    buyOrders.forEach((o) => {
      const stock = getStockFromSymbol(o.symbol);
      if (!stock) return;
      const key = `${o.symbol}_${o.strategy || "NO_STRATEGY"}`;
      if (!symbolStrategyBaseQty[key]) {
        const qty = Math.abs(parseFloat(o.quantity || o.qty || 0));
        symbolStrategyBaseQty[key] = {
          symbol: o.symbol,
          strategy: o.strategy,
          index: stock,
          baseQty: qty,
          clients: 1
        };
      }
    });

    return {
      data,
      activeStocks,
      activeIndexes: activeStocks,
      baseQtyMap: {},
      symbolStrategyBaseQty,
      buyOrdersCount: buyOrders.length,
      totalOrdersCount: stockOrders.length
    };
  }, [orders, userMappings, ordersUpdateVersion, users]);

  return stockOptionsOrderData;
}
